import type { ChatType } from '@kanbu/schema';
import type { EvalTestResults } from '@kanbu/schema/contracts';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import * as ExcelJS from 'exceljs';
import { useCallback, useMemo } from 'react';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Hook for generating and downloading the Excel file with the evaluation results.
 */
export function useEvaluationXls() {
  const { t } = useLingui();
  const detailHeaders = useMemo(() => {
    return [
      t`Thread ID`,
      t`Message index`,
      t`Question`,
      t`Answer`,
      t`Expected answer`,
      t`Correct`,
      t`Accuracy`,
      t`Explanation`,
      t`Latency`,
    ];
  }, [t]);

  /**
   * Generates the Excel workbook from the evaluation results
   */
  const generateWorkbook = useCallback(
    (data: EvalTestResults, chat?: ChatType) => {
      const wb = new ExcelJS.Workbook();

      // Generate summary data
      const filteredMessages = data.filter(
        test => test.messages.length > 0 && test.messages[0]?.results,
      );

      const totalMessages = filteredMessages.reduce(
        (acc, test) => acc + test.messages.length,
        0,
      );

      const totalCorrect = filteredMessages.reduce(
        (acc, test) =>
          acc + test.messages.filter(m => m.results.isCorrect).length,
        0,
      );

      let medianAccuracy = filteredMessages.reduce(
        (acc, test) => acc + test.messages[0].results.accuracyPercentage,
        0,
      );
      medianAccuracy = medianAccuracy > 0 ? medianAccuracy / data.length : 0;

      // Summary worksheet
      const summaryData = [
        ['Evaluation Summary'],
        ['Date', new Date().toLocaleString()],
        ['Total Messages', totalMessages],
        ['Correct Answers', totalCorrect],
        ['Incorrect Answers', totalMessages - totalCorrect],
        ['Median Accuracy', formatUtils.percentage(medianAccuracy)],
      ];

      // Add chat configuration if available
      if (chat) {
        summaryData.push(
          [''],
          ['Chat Configuration'],
          ['Chat ID', chat.id],
          ['Chat Name', chat.name],
          ['Agent', chat.agent],
          ['Model', chat.model],
          ...(Object.entries(chat.agentConfig || {}) as any)
            .filter(([key]: any[]) => !['systemPrompt', 'tone'].includes(key))
            .map(([key, value]: any[]) => [key, value]),
        );
      }

      const summaryWs = wb.addWorksheet('Summary');
      summaryWs.columns = [{ width: 15 }, { width: 50 }];
      summaryWs.addRows(summaryData);

      // Details worksheet
      const detailsData = data.flatMap((result, threadIndex) =>
        result.messages.map((message, messageIndex) => [
          `Thread ${threadIndex + 1}`,
          messageIndex + 1,
          message.question,
          message.answer,
          message.expectedOutput,
          message.results.isCorrect ? t`Yes` : t`No`,
          formatUtils.percentage(message.results.accuracyPercentage),
          message.results.explanation,
          `${message.latency}ms`,
        ]),
      );

      const detailsWs = wb.addWorksheet('Details');
      detailsWs.columns = detailHeaders.map(() => ({ width: 20 }));
      detailsWs.addRows([detailHeaders, ...detailsData]);

      return wb;
    },
    [detailHeaders, t],
  );

  /**
   * Downloads the Excel file with the evaluation results
   */
  const downloadXls = useCallback(
    async (data: EvalTestResults, chatId: string, chat?: ChatType) => {
      const wb = generateWorkbook(data, chat);
      const buffer = await wb.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME }));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${formatUtils.date(new Date(), DateFormat.DateInput)}_${t`evaluation`}_${chatId}_${chat?.agent}_${chat?.name}.xlsx`;
      window.document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    [generateWorkbook, t],
  );

  return { generateWorkbook, downloadXls };
}
